/**
 * Contrast-safe derivation of a provider's brand accent.
 *
 * WHY THIS EXISTS. `NormalizedOffer.accent` is free-text: it comes from
 * `PROVIDER_LIST` for code providers and from `affiliate_offers.accent` for D1
 * rows, which any operator can edit with `pnpm ops:affiliate-offers`. The
 * offer card used to paint that value straight into `backgroundColor` under a
 * hardcoded `text-white`, and into `color` for the eyebrow over `bg-card`.
 * Measured 2026-09-02, three of five shipped accents failed WCAG AA on the CTA
 * pill and two of five failed on the eyebrow — the eyebrow is `text-xs
 * font-semibold` (12px bold), which is NOT "large text", so 4.5:1 applies:
 *
 *   #16a34a wise      pill 3.30:1 ✗   eyebrow-on-dark-card 4.10:1 ✗
 *   #ee4d2d shopee    pill 3.66:1 ✗
 *   #4a90d9 visualcv  pill 3.34:1 ✗
 *   #1e3a8a deel                      eyebrow-on-dark-card 1.63:1 ✗
 *   #0f172a contra                    eyebrow-on-dark-card ~1.1:1 ✗
 *
 * These helpers keep the brand hue and move only lightness until the ratio
 * clears, so the card stays recognisably the partner's colour while staying
 * readable in both themes. Pure and dependency-free so the render path, the
 * ingress validators and the tests all share one implementation.
 */

/**
 * The `--card` token from src/app/globals.css (`:root` line 59 / `.dark` line
 * 95). Duplicated here because this module must stay React- and CSS-free;
 * `tests/unit/monetization/accent.test.ts` re-reads globals.css and fails if
 * these drift, so the copy cannot rot silently.
 */
export const CARD_LIGHT = "#f5f5f7";
export const CARD_DARK = "#1d1d1f";

/** WCAG AA for normal text. The eyebrow and CTA label are both normal text. */
export const AA_NORMAL = 4.5;

type Rgb = { r: number; g: number; b: number };

/**
 * Strict `#rgb` / `#rrggbb` parse, normalised to lowercase `#rrggbb`. Returns
 * null for anything else — a named colour, a `rgb()` string, an empty cell, a
 * typo. Callers at the ingress DROP the row rather than render an unknown
 * value, which is the same contract `parseAffiliateOfferRow` already applies
 * to malformed copy.
 */
export function normalizeAccent(raw: string | null | undefined): string | null {
	if (typeof raw !== "string") return null;
	const hex = raw.trim().toLowerCase();
	if (/^#[0-9a-f]{6}$/.test(hex)) return hex;
	if (/^#[0-9a-f]{3}$/.test(hex)) {
		return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
	}
	return null;
}

function toRgb(hex: string): Rgb {
	return {
		r: Number.parseInt(hex.slice(1, 3), 16),
		g: Number.parseInt(hex.slice(3, 5), 16),
		b: Number.parseInt(hex.slice(5, 7), 16),
	};
}

function toHex({ r, g, b }: Rgb): string {
	const c = (n: number) =>
		Math.max(0, Math.min(255, Math.round(n)))
			.toString(16)
			.padStart(2, "0");
	return `#${c(r)}${c(g)}${c(b)}`;
}

/** WCAG relative luminance. */
export function luminance(hex: string): number {
	const { r, g, b } = toRgb(hex);
	const channel = (v: number) => {
		const s = v / 255;
		return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two `#rrggbb` colours, 1..21. */
export function contrastRatio(a: string, b: string): number {
	const la = luminance(a);
	const lb = luminance(b);
	const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
	return (hi + 0.05) / (lo + 0.05);
}

/** Move every channel toward white (t > 0) or black (t < 0) by |t|. */
function shade(hex: string, t: number): string {
	const { r, g, b } = toRgb(hex);
	const target = t > 0 ? 255 : 0;
	const k = Math.abs(t);
	return toHex({
		r: r + (target - r) * k,
		g: g + (target - g) * k,
		b: b + (target - b) * k,
	});
}

/**
 * Nudge `hex` toward `direction` until it clears `min` against `against`,
 * returning the first step that does. Steps are small so the brand hue stays
 * recognisable; the last step is full black/white, which always clears.
 */
function shadeUntil(
	hex: string,
	against: string,
	min: number,
	direction: 1 | -1,
): string {
	// The step at t ≈ 1 is pure #000000 / #ffffff, so it is returned as-is.
	for (let t = 0.05; ; t += 0.05) {
		const candidate = shade(hex, direction * t);
		if (t >= 1 || contrastRatio(candidate, against) >= min) return candidate;
	}
}

/**
 * A CTA pill's background and its label colour. The background is the brand
 * accent untouched; the label is whichever of black/white reads on it.
 *
 * No shading is needed here, and that is a property of the maths rather than
 * an oversight: contrast against white is 1.05/(L+0.05) and against black is
 * (L+0.05)/0.05, so the two are equal at L ≈ 0.179 where BOTH read 4.58:1.
 * Every colour therefore clears AA against at least one of them. The bug this
 * replaces was not an impossible accent — it was the card hardcoding
 * `text-white` and never asking.
 */
export function accentPair(raw: string): {
	bg: string;
	fg: string;
	bgHover: string;
} {
	const hex = normalizeAccent(raw) ?? "#4f46e5";
	const fg =
		contrastRatio(hex, "#ffffff") >= contrastRatio(hex, "#000000")
			? "#ffffff"
			: "#000000";
	// Hover shades the fill AWAY from the label — darker under a white label,
	// lighter under a black one — so the pressed-toward state gains contrast
	// instead of losing it. The idiom it replaces was `hover:opacity-90`, which
	// DESIGN.md names as the weaker of the two shipped hover idioms precisely
	// because it fades the label along with the fill: on a 12px semibold label
	// already sitting near the AA floor, that is a contrast regression on hover.
	const bgHover = shade(hex, fg === "#ffffff" ? -0.12 : 0.12);
	return { bg: hex, fg, bgHover };
}

/**
 * The accent as TEXT on the card surface, one value per theme. Each is the
 * accent shaded until it clears AA against that theme's `--card`: darkened on
 * the light card, lightened on the dark one.
 */
export function accentOnCard(raw: string): { light: string; dark: string } {
	const hex = normalizeAccent(raw) ?? "#4f46e5";
	return {
		light:
			contrastRatio(hex, CARD_LIGHT) >= AA_NORMAL
				? hex
				: shadeUntil(hex, CARD_LIGHT, AA_NORMAL, -1),
		dark:
			contrastRatio(hex, CARD_DARK) >= AA_NORMAL
				? hex
				: shadeUntil(hex, CARD_DARK, AA_NORMAL, 1),
	};
}
